import { useRef, useState } from 'react'
import { useForm, usePage } from '@inertiajs/react'
import { ImageIcon, UploadCloud } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { t } from '@/shared/lib/i18n'
import { uploadImageAdminInstrumentTypePath } from '@/shared/lib/routes'
import { readImageDimensions, exceedsMaxDimensions } from './imageDimensions'

export interface InstrumentTypeImageUploadTarget {
  id: number
  hasImage: boolean
  imagePath: string | null
}

interface FormErrors {
  [key: string]: string[] | undefined
}

export const InstrumentTypeImageUploadSection = ({
  instrumentType,
}: {
  instrumentType: InstrumentTypeImageUploadTarget
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const imageForm = useForm<{ file: File | null }>({ file: null })
  const { errors: pageErrors } = usePage<{ errors?: FormErrors }>().props
  const canUpdate = useCan(Resources.ADMIN_INSTRUMENT_TYPE, Actions.UPDATE)
  const [dimensionError, setDimensionError] = useState<string | null>(null)

  const pageFileErrors = pageErrors?.file
  const imageFormFileError = imageForm.errors.file

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !instrumentType.id) {
      return
    }

    setDimensionError(null)

    // Client-side 400×400 guard (REBUILD-503). The server re-validates, so a file whose
    // dimensions can't be read here still gets checked on upload.
    const dimensions = await readImageDimensions(file)
    if (exceedsMaxDimensions(dimensions)) {
      setDimensionError(t('admin.instrumentTypes.image.dimensionsTooLarge'))
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }

    imageForm.transform(() => ({ file }))
    imageForm.post(uploadImageAdminInstrumentTypePath(instrumentType.id), {
      forceFormData: true,
      onSuccess: () => {
        imageForm.reset()
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      },
    })
  }

  return (
    <section className="space-y-3 rounded-lg border p-4">
      <h2 className="flex items-center gap-2 text-base font-semibold">
        <ImageIcon className="h-4 w-4 text-muted-foreground" />
        {t('admin.instrumentTypes.image.sectionTitle')}
      </h2>

      {instrumentType.imagePath ? (
        <img
          src={instrumentType.imagePath}
          alt={t('admin.instrumentTypes.image.currentImage')}
          className="max-h-40 rounded-md border object-contain"
        />
      ) : (
        <p className="text-sm text-muted-foreground">{t('admin.instrumentTypes.image.noImage')}</p>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/tiff"
        className="hidden"
        onChange={(event) => void handleFileChange(event)}
        disabled={imageForm.processing}
      />

      {(dimensionError || pageFileErrors?.[0] || imageFormFileError) && (
        <p className="text-sm text-destructive">
          {dimensionError ?? pageFileErrors?.[0] ?? imageFormFileError}
        </p>
      )}

      {canUpdate && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={imageForm.processing}
          onClick={() => fileInputRef.current?.click()}
        >
          <UploadCloud className="mr-2 h-4 w-4" />
          {imageForm.processing
            ? '…'
            : instrumentType.hasImage
              ? t('admin.instrumentTypes.image.changeButton')
              : t('admin.instrumentTypes.image.uploadButton')}
        </Button>
      )}
      <p className="text-xs text-muted-foreground">
        {t('admin.instrumentTypes.image.allowedTypes')}
      </p>
    </section>
  )
}
